import { format } from 'date-fns';

export const EVENT_TYPES = {
  STATE_CHANGE: 'state_change',
  CUSTOM: 'custom',
  CALCULATED_STATE_CHANGE: 'calculated_state_change',
};

export function formatStateDescription(stateCode) {
  if (!stateCode) return 'Unknown State';
  const code = String(stateCode).replace(/([a-z])([A-Z])/g, '$1_$2').toLowerCase();
  return code
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function getStateChangeDescription(event) {
  let from;
  let to;
  if (Array.isArray(event.property_summary?.single_properties)) {
    const stateProp = event.property_summary.single_properties.find(
      p => p.property === 'shipment.shipment_state.state',
    );
    if (stateProp) {
      from = stateProp.previous_value;
      to = stateProp.value;
    }
  }
  if (from && to) {
    if (from === to) {
      return formatStateDescription(to);
    }
    return `${formatStateDescription(from)} to ${formatStateDescription(to)}`;
  }
  return '';
}

function getTransitionLabel(prop, isNow, was, label, noLongerLabel) {
  if (prop && isNow !== was) {
    return isNow ? label : noLongerLabel;
  }
  return null;
}

function getCalculatedStateChangeDescription(event) {
  const props = event.property_summary?.single_properties || [];
  const getProp = key => props.find(p => p.property === key);
  const parseBool = v => String(v).toLowerCase() === 'true';

  const isLateProp = getProp('shipment.lateness.is_late');
  const isMissingProp = getProp('shipment.may_be_missing');

  const isLate = isLateProp ? parseBool(isLateProp.value) : undefined;
  const wasLate = isLateProp ? parseBool(isLateProp.previous_value) : undefined;
  const isMissing = isMissingProp ? parseBool(isMissingProp.value) : undefined;
  const wasMissing = isMissingProp ? parseBool(isMissingProp.previous_value) : undefined;

  if (isLateProp && isLate !== wasLate && isMissingProp && isMissing !== wasMissing) {
    if (isLate && isMissing) return 'Marked as Late and Marked as May Be Missing';
    if (isLate) return 'Marked as Late';
    if (isMissing) return 'Marked as May Be Missing';
  }

  const lateLabel = getTransitionLabel(isLateProp, isLate, wasLate, 'Marked as Late', 'No Longer Late');
  if (lateLabel) return lateLabel;

  const missingLabel = getTransitionLabel(isMissingProp, isMissing, wasMissing, 'Marked as May Be Missing', 'No Longer Marked as May Be Missing');
  if (missingLabel) return missingLabel;

  // If no explicit transition, return generic label
  return 'Calculated State Change';
}

export function getEventDescription(event) {
  const type = String(event.event_type).toLowerCase();
  switch (type) {
    case EVENT_TYPES.STATE_CHANGE:
      return getStateChangeDescription(event);
    case EVENT_TYPES.CALCULATED_STATE_CHANGE:
      return getCalculatedStateChangeDescription(event);
    case EVENT_TYPES.CUSTOM:
      return 'Custom Event';
    default:
      return '';
  }
}

export function getDotColor(eventType) {
  const type = String(eventType).toLowerCase();
  switch (type) {
    case EVENT_TYPES.STATE_CHANGE:
      return '#C6D4E1';
    case EVENT_TYPES.CUSTOM:
      return '#A95AA1';
    case EVENT_TYPES.CALCULATED_STATE_CHANGE:
      return '#44749D';
    default:
      return '#EBE7E0';
  }
}

export function formatDisplayDate(dateString) {
  if (!dateString || dateString === '0001-01-01T00:00:00+00:00') return '';
  const cleaned = dateString.replace(/\.\d+(?=[Z+-])/, '');
  const date = new Date(cleaned);
  if (!Number.isNaN(date.getTime())) {
    try {
      return format(date, 'DD/MM/YYYY HH:mm:ss');
    } catch (e) {
      return '';
    }
  }
  return '';
}

export function getFormattedTimestamp(event) {
  if (event.created_on) {
    const formatted = formatDisplayDate(event.created_on);
    if (formatted) return formatted;
  }
  return formatDisplayDate(event.timestamp);
}

export function filterValidEvents(events) {
  return (events || []).filter(event => {
    if (!event) return false;
    const isNullId = event.id == null;
    return !(isNullId);
  });
}
